import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Warehouse, Box, Package, ArrowRightLeft, TrendingUp, TrendingDown, 
  Plus, Search, QrCode, ScanLine, ArrowRight, AlertTriangle,
  History, FileText, Download, Truck, ClipboardList, ShoppingCart, Tag, X
} from 'lucide-react';
import WMSDashboard from '../components/wms/WMSDashboard';
import InventoryTab from '../components/wms/InventoryTab';
import OrdersTab from '../components/wms/OrdersTab';
import LabelsTab from '../components/wms/LabelsTab';
import BinsPageEnhanced from './settings/BinsPage';

const WMSPage = () => {
  const { plants, selectedPlant } = useData();
  const [bins, setBins] = useState([]);
  const [articles, setArticles] = useState([]);
  const [stockEntries, setStockEntries] = useState([]);
  const [movements, setMovements] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Article search for dialogs
  const [articleSearchTerm, setArticleSearchTerm] = useState('');
  
  // Dialogs
  const [showAddBinDialog, setShowAddBinDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  
  // Forms
  const [binForm, setBinForm] = useState({
    plant_id: '',
    shelf: '',
    cabinet: '',
    position: '',
    description: ''
  });
  
  const [transferForm, setTransferForm] = useState({
    article_id: '',
    article_name: '',
    from_bin_id: '',
    to_bin_id: '',
    quantity: 0,
    notes: ''
  });
  
  const [receiveForm, setReceiveForm] = useState({
    article_id: '',
    article_name: '',
    bin_id: '',
    quantity: 0,
    lot_number: '',
    supplier_id: '',
    notes: ''
  });
  
  const [issueForm, setIssueForm] = useState({
    article_id: '',
    article_name: '',
    bin_id: '',
    quantity: 0,
    reference_type: 'revision',
    reference_id: '',
    notes: ''
  });
  
  const [adjustForm, setAdjustForm] = useState({
    article_id: '',
    bin_id: '',
    new_quantity: 0,
    reason: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedPlant && selectedPlant !== 'all' ? { plant_id: selectedPlant } : {};
      
      const [binsRes, articlesRes, stockRes, movementsRes] = await Promise.all([
        api.get('/bins', { params }),
        api.get('/articles'),
        api.get('/stock', { params }),
        api.get('/stock-movements', { params })
      ]);
      
      setBins(binsRes.data);
      setArticles(articlesRes.data);
      setStockEntries(stockRes.data);
      setMovements(movementsRes.data);
      
      // Calculate low stock items
      const lowStock = articlesRes.data.filter(article => {
        const totalStock = stockRes.data
          .filter(s => s.article_id === article.id)
          .reduce((sum, s) => sum + (s.quantity || 0), 0);
        return totalStock <= (article.min_stock || 0);
      });
      setLowStockItems(lowStock);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [selectedPlant]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Add BIN
  const handleAddBin = async (e) => {
    e.preventDefault();
    try {
      const plantName = plants.find(p => p.id === binForm.plant_id)?.name || 'BIN';
      const code = `${plantName.substring(0, 3).toUpperCase()}-${binForm.shelf}${binForm.cabinet}${binForm.position}`;
      await api.post('/bins', { ...binForm, code });
      toast.success('Locatie stoc creata!');
      setShowAddBinDialog(false);
      setBinForm({ plant_id: '', shelf: '', cabinet: '', position: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la creare');
    }
  };

  // Stock Transfer
  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await api.post('/stock/transfer', transferForm);
      toast.success('Transfer efectuat cu succes!');
      setShowTransferDialog(false);
      setTransferForm({ article_id: '', from_bin_id: '', to_bin_id: '', quantity: 0, notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la transfer');
    }
  };

  // Stock Receive (IN)
  const handleReceive = async (e) => {
    e.preventDefault();
    try {
      await api.post('/stock/receive', receiveForm);
      toast.success('Receptie inregistrata!');
      setShowReceiveDialog(false);
      setReceiveForm({ article_id: '', bin_id: '', quantity: 0, lot_number: '', supplier_id: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la receptie');
    }
  };

  // Stock Issue (OUT)
  const handleIssue = async (e) => {
    e.preventDefault();
    try {
      await api.post('/stock/issue', issueForm);
      toast.success('Eliberare inregistrata!');
      setShowIssueDialog(false);
      setIssueForm({ article_id: '', bin_id: '', quantity: 0, reference_type: 'revision', reference_id: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la eliberare');
    }
  };

  // Stock Adjustment
  const handleAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.post('/stock/adjust', adjustForm);
      toast.success('Ajustare inregistrata!');
      setShowAdjustDialog(false);
      setAdjustForm({ article_id: '', bin_id: '', new_quantity: 0, reason: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ajustare');
    }
  };

  // Get available quantity for an article in a specific bin
  const getAvailableQty = (articleId, binId) => {
    const entry = stockEntries.find(s => s.article_id === articleId && s.bin_id === binId);
    return entry?.quantity || 0;
  };

  // Get total stock for an article
  const getTotalStock = (articleId) => {
    return stockEntries
      .filter(s => s.article_id === articleId)
      .reduce((sum, s) => sum + (s.quantity || 0), 0);
  };

  // Get bins with stock for an article
  const getBinsWithStock = (articleId) => {
    return stockEntries
      .filter(s => s.article_id === articleId && s.quantity > 0)
      .map(s => ({
        ...s,
        bin: bins.find(b => b.id === s.bin_id)
      }));
  };

  const getMovementTypeLabel = (type) => {
    const labels = {
      in: 'Receptie',
      out: 'Eliberare',
      transfer: 'Transfer',
      adjustment: 'Ajustare'
    };
    return labels[type] || type;
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'in': return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'out': return <TrendingDown className="w-4 h-4 text-red-600" />;
      case 'transfer': return <ArrowRightLeft className="w-4 h-4 text-blue-600" />;
      default: return <FileText className="w-4 h-4 text-slate-600" />;
    }
  };

  // Filter articles by search
  const filteredArticles = articles.filter(a =>
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="wms-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">WMS - Gestiune Stocuri</h1>
          <p className="text-slate-600 mt-1">Warehouse Management System - Gestionare avansata stocuri</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild className="text-purple-600 border-purple-600 hover:bg-purple-50">
            <a href="/wms/scanner">
              <ScanLine className="w-4 h-4 mr-2" />
              Scanner Rapid
            </a>
          </Button>
          <Button variant="outline" onClick={() => setShowAddBinDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Locatie BIN
          </Button>
          <Button variant="outline" onClick={() => setShowReceiveDialog(true)} className="text-green-600 border-green-600 hover:bg-green-50">
            <TrendingUp className="w-4 h-4 mr-2" />
            Receptie
          </Button>
          <Button variant="outline" onClick={() => setShowIssueDialog(true)} className="text-red-600 border-red-600 hover:bg-red-50">
            <TrendingDown className="w-4 h-4 mr-2" />
            Eliberare
          </Button>
          <Button onClick={() => setShowTransferDialog(true)} className="btn-primary">
            <ArrowRightLeft className="w-4 h-4 mr-2" />
            Transfer
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Box className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{bins.length}</p>
                <p className="text-sm text-slate-500">Locatii BIN</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{articles.length}</p>
                <p className="text-sm text-slate-500">Articole</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <History className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{movements.length}</p>
                <p className="text-sm text-slate-500">Miscari luna</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`card-industrial ${lowStockItems.length > 0 ? 'border-orange-500' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${lowStockItems.length > 0 ? 'bg-orange-100' : 'bg-slate-100'}`}>
                <AlertTriangle className={`w-5 h-5 ${lowStockItems.length > 0 ? 'text-orange-600' : 'text-slate-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{lowStockItems.length}</p>
                <p className="text-sm text-slate-500">Stoc minim</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="grid w-full grid-cols-8 h-12">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="stock">Situatie Stoc</TabsTrigger>
          <TabsTrigger value="bins">Locatii BIN</TabsTrigger>
          <TabsTrigger value="movements">Miscari</TabsTrigger>
          <TabsTrigger value="alerts">Alerte ({lowStockItems.length})</TabsTrigger>
          <TabsTrigger value="inventory" className="flex items-center gap-1">
            <ClipboardList className="w-4 h-4" />
            Inventar
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-1">
            <ShoppingCart className="w-4 h-4" />
            Comenzi
          </TabsTrigger>
          <TabsTrigger value="labels" className="flex items-center gap-1">
            <Tag className="w-4 h-4" />
            Etichete
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <WMSDashboard />
        </TabsContent>

        {/* Stock Overview Tab */}
        <TabsContent value="stock" className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Cauta articol (nume sau cod)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12"
            />
          </div>
          
          <div className="grid gap-3">
            {filteredArticles.map((article) => {
              const totalStock = getTotalStock(article.id);
              const binsWithStock = getBinsWithStock(article.id);
              const isLowStock = totalStock <= (article.min_stock || 0);
              
              return (
                <Card key={article.id} className={`card-industrial ${isLowStock ? 'border-orange-400' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isLowStock ? 'bg-orange-100' : 'bg-slate-100'}`}>
                          <Package className={`w-6 h-6 ${isLowStock ? 'text-orange-600' : 'text-slate-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{article.name}</h3>
                            {isLowStock && <Badge variant="destructive" className="text-xs">STOC MINIM</Badge>}
                          </div>
                          <p className="text-sm text-slate-500">{article.code} | Min: {article.min_stock || 0} {article.unit}</p>
                          {binsWithStock.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {binsWithStock.map((s, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {s.bin?.code}: {s.quantity} {article.unit}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${isLowStock ? 'text-orange-600' : 'text-slate-900'}`}>
                          {totalStock}
                        </p>
                        <p className="text-sm text-slate-500">{article.unit}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* BIN Locations Tab - ENHANCED */}
        <TabsContent value="bins" className="space-y-4">
          <BinsPageEnhanced />
        </TabsContent>

        {/* Movements Tab */}
        <TabsContent value="movements" className="space-y-4">
          <div className="space-y-2">
            {movements.slice(0, 20).map((mov, i) => {
              const article = articles.find(a => a.id === mov.article_id);
              const fromBin = bins.find(b => b.id === mov.from_bin_id);
              const toBin = bins.find(b => b.id === mov.to_bin_id);
              
              return (
                <Card key={i} className="card-industrial">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getMovementIcon(mov.movement_type)}
                        <div>
                          <p className="font-medium">{article?.name || 'Articol necunoscut'}</p>
                          <p className="text-sm text-slate-500">
                            {mov.movement_type === 'transfer' 
                              ? `${fromBin?.code || '?'} → ${toBin?.code || '?'}`
                              : mov.movement_type === 'in' 
                                ? `→ ${toBin?.code || '?'}`
                                : `${fromBin?.code || '?'} →`
                            }
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${mov.movement_type === 'in' ? 'text-green-600' : mov.movement_type === 'out' ? 'text-red-600' : 'text-blue-600'}`}>
                          {mov.movement_type === 'in' ? '+' : mov.movement_type === 'out' ? '-' : ''}{mov.quantity}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(mov.created_at).toLocaleDateString('ro-RO')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {movements.length === 0 && (
              <Card className="card-industrial">
                <CardContent className="py-12 text-center">
                  <History className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">Nu exista miscari inregistrate</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          {lowStockItems.length > 0 ? (
            <div className="space-y-3">
              {lowStockItems.map((article) => {
                const totalStock = getTotalStock(article.id);
                return (
                  <Card key={article.id} className="card-industrial border-orange-400">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-6 h-6 text-orange-600" />
                          <div>
                            <h3 className="font-semibold">{article.name}</h3>
                            <p className="text-sm text-slate-500">{article.code}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg">
                            <span className="text-orange-600 font-bold">{totalStock}</span>
                            <span className="text-slate-400"> / {article.min_stock || 0}</span>
                          </p>
                          <p className="text-xs text-slate-500">{article.unit}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="card-industrial">
              <CardContent className="py-12 text-center">
                <Package className="w-12 h-12 text-green-300 mx-auto mb-2" />
                <p className="text-green-600 font-medium">Toate stocurile sunt OK</p>
                <p className="text-sm text-slate-500">Nu exista articole sub stocul minim</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          <InventoryTab plants={plants} />
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <OrdersTab />
        </TabsContent>

        {/* Labels Tab */}
        <TabsContent value="labels" className="space-y-4">
          <LabelsTab plants={plants} bins={bins} />
        </TabsContent>
      </Tabs>

      {/* Add BIN Dialog */}
      <Dialog open={showAddBinDialog} onOpenChange={setShowAddBinDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adauga Locatie BIN</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBin} className="space-y-4">
            <div className="space-y-2">
              <Label>Sediu *</Label>
              <Select value={binForm.plant_id} onValueChange={(v) => setBinForm({ ...binForm, plant_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecteaza" /></SelectTrigger>
                <SelectContent>
                  {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Raft</Label>
                <Input value={binForm.shelf} onChange={(e) => setBinForm({ ...binForm, shelf: e.target.value.toUpperCase() })} maxLength={4} placeholder="RA01" />
              </div>
              <div className="space-y-2">
                <Label>Dulap</Label>
                <Input value={binForm.cabinet} onChange={(e) => setBinForm({ ...binForm, cabinet: e.target.value.toUpperCase() })} maxLength={4} placeholder="DU01" />
              </div>
              <div className="space-y-2">
                <Label>Pozitie</Label>
                <Input value={binForm.position} onChange={(e) => setBinForm({ ...binForm, position: e.target.value.toUpperCase() })} maxLength={4} placeholder="P01" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddBinDialog(false)}>Anuleaza</Button>
              <Button type="submit" className="btn-primary">Adauga</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={(open) => {
        setShowTransferDialog(open);
        if (!open) setArticleSearchTerm('');
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Transfer Stoc
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTransfer} className="space-y-4">
            <div className="space-y-2">
              <Label>Articol *</Label>
              {transferForm.article_id ? (
                <div className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{transferForm.article_name}</p>
                    <p className="text-xs text-slate-500">{articles.find(a => a.id === transferForm.article_id)?.code}</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setTransferForm({ ...transferForm, article_id: '', article_name: '' })}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={articleSearchTerm}
                      onChange={(e) => setArticleSearchTerm(e.target.value)}
                      placeholder="Cautati articol..."
                      className="pl-10"
                    />
                  </div>
                  {articleSearchTerm && (
                    <div className="border rounded-lg divide-y bg-white max-h-40 overflow-y-auto">
                      {articles.filter(a => 
                        a.name?.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
                        a.code?.toLowerCase().includes(articleSearchTerm.toLowerCase())
                      ).slice(0, 5).map(article => (
                        <div 
                          key={article.id}
                          onClick={() => {
                            setTransferForm({ ...transferForm, article_id: article.id, article_name: article.name });
                            setArticleSearchTerm('');
                          }}
                          className="p-2 hover:bg-blue-50 cursor-pointer text-sm"
                        >
                          <p className="font-medium">{article.name}</p>
                          <p className="text-xs text-slate-500">Cod: {article.code} | Stoc: {article.total_stock || 0}</p>
                        </div>
                      ))}
                      {articles.filter(a => 
                        a.name?.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
                        a.code?.toLowerCase().includes(articleSearchTerm.toLowerCase())
                      ).length === 0 && (
                        <div className="p-2 text-center text-sm text-slate-500">Niciun articol gasit</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Din locatia *</Label>
                <Select value={transferForm.from_bin_id} onValueChange={(v) => setTransferForm({ ...transferForm, from_bin_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Sursa" /></SelectTrigger>
                  <SelectContent>
                    {bins.map((b) => <SelectItem key={b.id} value={b.id}>{b.code}</SelectItem>)}
                  </SelectContent>
                </Select>
                {transferForm.article_id && transferForm.from_bin_id && (
                  <p className="text-xs text-slate-500">Disponibil: {getAvailableQty(transferForm.article_id, transferForm.from_bin_id)}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>In locatia *</Label>
                <Select value={transferForm.to_bin_id} onValueChange={(v) => setTransferForm({ ...transferForm, to_bin_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Destinatie" /></SelectTrigger>
                  <SelectContent>
                    {bins.filter(b => b.id !== transferForm.from_bin_id).map((b) => <SelectItem key={b.id} value={b.id}>{b.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cantitate *</Label>
              <Input type="number" value={transferForm.quantity} onChange={(e) => setTransferForm({ ...transferForm, quantity: parseInt(e.target.value) || 0 })} min="1" step="1" />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={transferForm.notes} onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTransferDialog(false)}>Anuleaza</Button>
              <Button type="submit" className="btn-primary">Transfera</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={(open) => {
        setShowReceiveDialog(open);
        if (!open) setArticleSearchTerm('');
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Receptie Stoc
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReceive} className="space-y-4">
            <div className="space-y-2">
              <Label>Articol *</Label>
              {receiveForm.article_id ? (
                <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{receiveForm.article_name}</p>
                    <p className="text-xs text-slate-500">{articles.find(a => a.id === receiveForm.article_id)?.code}</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setReceiveForm({ ...receiveForm, article_id: '', article_name: '' })}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={articleSearchTerm}
                      onChange={(e) => setArticleSearchTerm(e.target.value)}
                      placeholder="Cautati articol..."
                      className="pl-10"
                    />
                  </div>
                  {articleSearchTerm && (
                    <div className="border rounded-lg divide-y bg-white max-h-40 overflow-y-auto">
                      {articles.filter(a => 
                        a.name?.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
                        a.code?.toLowerCase().includes(articleSearchTerm.toLowerCase())
                      ).slice(0, 5).map(article => (
                        <div 
                          key={article.id}
                          onClick={() => {
                            setReceiveForm({ 
                              ...receiveForm, 
                              article_id: article.id, 
                              article_name: article.name,
                              bin_id: article.default_bin_id || receiveForm.bin_id
                            });
                            setArticleSearchTerm('');
                          }}
                          className="p-2 hover:bg-green-50 cursor-pointer text-sm"
                        >
                          <p className="font-medium">{article.name}</p>
                          <p className="text-xs text-slate-500">Cod: {article.code} | Stoc: {article.total_stock || 0}</p>
                        </div>
                      ))}
                      {articles.filter(a => 
                        a.name?.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
                        a.code?.toLowerCase().includes(articleSearchTerm.toLowerCase())
                      ).length === 0 && (
                        <div className="p-2 text-center text-sm text-slate-500">Niciun articol gasit</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Locatie BIN *</Label>
              <Select value={receiveForm.bin_id} onValueChange={(v) => setReceiveForm({ ...receiveForm, bin_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecteaza locatia" /></SelectTrigger>
                <SelectContent>
                  {bins.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} - {b.plant_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cantitate *</Label>
                <Input type="number" value={receiveForm.quantity} onChange={(e) => setReceiveForm({ ...receiveForm, quantity: parseInt(e.target.value) || 0 })} min="1" step="1" />
              </div>
              <div className="space-y-2">
                <Label>Nr. Lot</Label>
                <Input value={receiveForm.lot_number} onChange={(e) => setReceiveForm({ ...receiveForm, lot_number: e.target.value })} placeholder="LOT-001" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={receiveForm.notes} onChange={(e) => setReceiveForm({ ...receiveForm, notes: e.target.value })} rows={2} placeholder="Factura, observatii..." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowReceiveDialog(false)}>Anuleaza</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">Receptioneaza</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Issue Dialog */}
      <Dialog open={showIssueDialog} onOpenChange={(open) => {
        setShowIssueDialog(open);
        if (!open) setArticleSearchTerm('');
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              Eliberare Stoc
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleIssue} className="space-y-4">
            <div className="space-y-2">
              <Label>Articol *</Label>
              {issueForm.article_id ? (
                <div className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg">
                  <div>
                    <p className="font-medium text-sm">{issueForm.article_name}</p>
                    <p className="text-xs text-slate-500">{articles.find(a => a.id === issueForm.article_id)?.code}</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setIssueForm({ ...issueForm, article_id: '', article_name: '' })}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={articleSearchTerm}
                      onChange={(e) => setArticleSearchTerm(e.target.value)}
                      placeholder="Cautati articol..."
                      className="pl-10"
                    />
                  </div>
                  {articleSearchTerm && (
                    <div className="border rounded-lg divide-y bg-white max-h-40 overflow-y-auto">
                      {articles.filter(a => 
                        a.name?.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
                        a.code?.toLowerCase().includes(articleSearchTerm.toLowerCase())
                      ).slice(0, 5).map(article => (
                        <div 
                          key={article.id}
                          onClick={() => {
                            setIssueForm({ 
                              ...issueForm, 
                              article_id: article.id, 
                              article_name: article.name,
                              bin_id: article.default_bin_id || issueForm.bin_id
                            });
                            setArticleSearchTerm('');
                          }}
                          className="p-2 hover:bg-red-50 cursor-pointer text-sm"
                        >
                          <p className="font-medium">{article.name}</p>
                          <p className="text-xs text-slate-500">Cod: {article.code} | Stoc: {article.total_stock || 0}</p>
                        </div>
                      ))}
                      {articles.filter(a => 
                        a.name?.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
                        a.code?.toLowerCase().includes(articleSearchTerm.toLowerCase())
                      ).length === 0 && (
                        <div className="p-2 text-center text-sm text-slate-500">Niciun articol gasit</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Din locatia BIN *</Label>
              <Select value={issueForm.bin_id} onValueChange={(v) => setIssueForm({ ...issueForm, bin_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecteaza locatia" /></SelectTrigger>
                <SelectContent>
                  {bins.map((b) => <SelectItem key={b.id} value={b.id}>{b.code}</SelectItem>)}
                </SelectContent>
              </Select>
              {issueForm.article_id && issueForm.bin_id && (
                <>
                  <p className="text-xs text-slate-500">Disponibil: {getAvailableQty(issueForm.article_id, issueForm.bin_id)}</p>
                  {articles.find(a => a.id === issueForm.article_id)?.default_bin_id === issueForm.bin_id && (
                    <p className="text-xs text-green-600 flex items-center gap-1">BIN preferat - scadere prioritara</p>
                  )}
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label>Cantitate *</Label>
              <Input type="number" value={issueForm.quantity} onChange={(e) => setIssueForm({ ...issueForm, quantity: parseInt(e.target.value) || 0 })} min="1" step="1" />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={issueForm.notes} onChange={(e) => setIssueForm({ ...issueForm, notes: e.target.value })} rows={2} placeholder="Referinta revizie, motiv..." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowIssueDialog(false)}>Anuleaza</Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white">Elibereaza</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WMSPage;
